import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Layers, ArrowUpRight } from "lucide-react";
import { abs, ISSUE_ICONS, STATUS_TONE } from "@/lib/issues";

const HEIGHTS = ["h-[300px]", "h-[220px]", "h-[260px]", "h-[340px]", "h-[240px]"];

export function AiBadge({ className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border border-npst-goldBorder bg-white/90 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-npst-gold backdrop-blur-md ${className}`} data-testid="issue-card-ai-badge">
      <Sparkles size={10} /> AI illustration
    </span>
  );
}

export function IssueVisual({ issue, thumb = true, className = "" }) {
  const Icon = ISSUE_ICONS[issue.icon] || Layers;
  const src = abs(thumb ? issue.thumb_url : issue.image_url);
  if (src) return <img src={src} alt="" loading="lazy" className={`h-full w-full object-cover ${className}`} />;
  return (
    <div className={`flex h-full w-full items-center justify-center bg-npst-goldLight ${className}`}>
      <Icon size={64} strokeWidth={1} className="text-npst-gold/50" />
    </div>
  );
}

export default function IssueCard({ issue, index, onOpen }) {
  const Icon = ISSUE_ICONS[issue.icon] || Layers;
  const total = Object.values(issue.counts || {}).reduce((a, b) => a + b, 0);
  return (
    <motion.div layout initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.5, delay: Math.min(index, 8) * 0.05, ease: [0.215, 0.61, 0.355, 1] }}
      className="mb-6 break-inside-avoid">
      <button type="button" onClick={() => onOpen(issue)} data-testid={`issue-card-${issue.slug}`} aria-label={`Open ${issue.title}`}
        className="group hover-lift block w-full overflow-hidden rounded-xl border border-npst-line bg-white text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-npst-gold">
        <div className={`relative overflow-hidden ${HEIGHTS[index % HEIGHTS.length]}`}>
          <div className="absolute inset-0 transition-transform duration-[1000ms] ease-out group-hover:scale-[1.05]"><IssueVisual issue={issue} /></div>
          <AiBadge className="absolute left-3 top-3 z-10" />
        </div>
        <div className="p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="inline-flex min-w-0 items-center gap-2 label-eyebrow text-npst-gold"><Icon size={14} className="shrink-0" /><span className="truncate">{issue.category}</span></span>
            <span className="shrink-0 rounded-full bg-npst-bgAlt px-2 py-0.5 font-mono text-[10px] text-npst-muted">{total} rec</span>
          </div>
          <h3 className="font-serif text-xl font-semibold leading-snug text-npst-ink transition-colors group-hover:text-npst-navy">{issue.title}</h3>
          <div className="mt-4 flex items-center justify-between">
            <span className="inline-flex items-center gap-2 rounded-full border border-npst-line bg-npst-bg px-2.5 py-1 text-[11px] text-npst-inkSoft" data-testid="issue-card-status">
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS_TONE[issue.status_kind] || "bg-npst-gold"}`} />{issue.status}
            </span>
            <ArrowUpRight size={16} className="text-npst-gold transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </div>
        </div>
      </button>
    </motion.div>
  );
}
